import React, { useRef, useState, useCallback } from 'react';
import { useThree } from '@react-three/fiber';
import { Circle } from '@react-three/drei';
import * as THREE from 'three';
import { POINT_COLORS, SIZES, POINT_TYPES } from '../utils/constants';

export function Point3D({
  point,
  isSelected = false,
  isHovered = false,
  isPending = false,
  onSelect,
  onHover,
  onDragStart,
  onDrag,
  onDragEnd,
  draggable = true,
  radius: customRadius,
}) {
  const meshRef = useRef();
  const [isDragging, setIsDragging] = useState(false);
  const { camera, gl } = useThree();

  // Ist es ein Hilfspunkt (für Schächte/Vorwände)?
  const isAuxiliary = point.type === POINT_TYPES.AUXILIARY;

  // Ist es ein Wand-Außenpunkt?
  const isWallOuter = point.type === POINT_TYPES.WALL_OUTER;

  // Ist es ein Kontrollpunkt (für Geometrie wie Rectangle, Polygon, etc.)?
  const isControlPoint = point.attributes?.controlPoint === true || point.controlPoint === true;

  // Ist es ein Arc-Through-Point (Scheitelpunkt eines Bogens)?
  const isArcThroughPoint = point.isArcThroughPoint === true;

  // Ist es ein Arc-Zwischenpunkt (nicht editierbar)?
  const isArcIntermediate = point.isArcIntermediate === true;

  // Ist es ein Arc-Endpoint (Start oder Ende)?
  const isArcEndpoint = point.isArcEndpoint === true;

  // Farbe bestimmen
  const getColor = () => {
    if (isPending) return POINT_COLORS.pending;
    if (isWallOuter) {
      // Wand-Außenpunkte: Orange
      if (isSelected) return POINT_COLORS.outerSelected;
      if (isHovered) return POINT_COLORS.outerHover;
      return POINT_COLORS.outer;
    }
    if (isArcThroughPoint) {
      // Arc-Scheitelpunkt: Orange/Gelb
      if (isSelected) return '#f59e0b'; // amber-500
      if (isHovered) return '#fbbf24'; // amber-400
      return '#f97316'; // orange-500
    }
    if (isArcIntermediate) {
      // Arc-Zwischenpunkte: dezent grau
      if (isSelected) return '#6b7280'; // gray-500
      if (isHovered) return '#9ca3af'; // gray-400
      return '#4b5563'; // gray-600
    }
    if (isArcEndpoint) {
      // Arc-Endpunkte (Start/Ende): Cyan/Türkis
      if (isSelected) return '#0891b2'; // cyan-600
      if (isHovered) return '#22d3ee'; // cyan-400
      return '#06b6d4'; // cyan-500
    }
    if (isAuxiliary) {
      if (isSelected) return POINT_COLORS.auxiliarySelected;
      if (isHovered) return POINT_COLORS.auxiliaryHover;
      return POINT_COLORS.auxiliary;
    }
    if (isControlPoint) {
      // Kontrollpunkte (für Geometrie): Grün
      if (isSelected) return '#15803d'; // green-700
      if (isHovered) return '#4ade80'; // green-400
      return '#22c55e'; // green-500
    }
    if (isSelected) return POINT_COLORS.selected;
    if (isHovered) return POINT_COLORS.hover;
    return POINT_COLORS.normal;
  };

  // Radius aus Props oder Default
  // Außenpunkte sind etwas kleiner
  const baseRadius = customRadius || (isWallOuter ? SIZES.pointRadius * 0.7 : SIZES.pointRadius);
  const radius = isSelected ? baseRadius * 1.3 : baseRadius;

  // Außenpunkte sind nicht direkt verschiebbar (bewegen sich mit Innenpunkten)
  const effectiveDraggable = draggable && !isWallOuter;

  // Mausposition zu Weltkoordinaten (mit THREE.Vector3.unproject)
  const getWorldPosition = useCallback((event) => {
    const rect = gl.domElement.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    const vector = new THREE.Vector3(x, y, 0);
    vector.unproject(camera);

    return { x: vector.x, y: vector.y };
  }, [camera, gl]);

  const handlePointerDown = useCallback((e) => {
    if (!effectiveDraggable) return;

    e.stopPropagation();
    setIsDragging(true);
    onDragStart?.(point.id);

    // Letzte Drag-Position speichern (für onDragEnd)
    let lastDragPos = { x: point.x, y: point.y };

    // Mausbewegung global tracken
    const handlePointerMove = (moveEvent) => {
      const pos = getWorldPosition(moveEvent);
      lastDragPos = { x: pos.x, y: pos.y };
      onDrag?.(point.id, pos.x, pos.y);
    };

    const handlePointerUp = () => {
      setIsDragging(false);
      // Finale Position übergeben (nicht aus React-State lesen, da möglicherweise veraltet!)
      onDragEnd?.(point.id, lastDragPos.x, lastDragPos.y);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  }, [effectiveDraggable, point.id, point.x, point.y, getWorldPosition, onDragStart, onDrag, onDragEnd]);

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    // Pass native event for shift-key detection
    onSelect?.(point.id, e.nativeEvent || e);
  }, [point.id, onSelect]);

  const handlePointerOver = useCallback((e) => {
    e.stopPropagation();
    onHover?.('point', point.id);
    document.body.style.cursor = effectiveDraggable ? 'grab' : 'pointer';
  }, [point.id, onHover, effectiveDraggable]);

  const handlePointerOut = useCallback(() => {
    onHover?.(null, null);
    document.body.style.cursor = 'default';
  }, [onHover]);

  // Arc-Through-Point (Scheitelpunkt): Kreis mit Pfeil nach außen
  if (isArcThroughPoint) {
    return (
      <group position={[point.x, point.y, 6]}>
        {/* Äußerer Ring */}
        <Circle
          ref={meshRef}
          args={[radius * 1.3, 32]}
          onClick={handleClick}
          onPointerDown={handlePointerDown}
          onPointerOver={handlePointerOver}
          onPointerOut={handlePointerOut}
        >
          <meshBasicMaterial
            color={getColor()}
            transparent
            opacity={0.9}
          />
        </Circle>

        {/* Innerer Punkt */}
        <Circle args={[radius * 0.5, 16]} position={[0, 0, 1]}>
          <meshBasicMaterial color="#b45309" />
        </Circle>

        {/* Pfeil-Symbol in Z-Richtung (nach oben aus der Zeichenebene) */}
        <line position={[0, 0, 2]}>
          <bufferGeometry>
            <bufferAttribute
              attach="attributes-position"
              count={6}
              array={new Float32Array([
                // Pfeil in Z-Richtung (nach oben)
                0, 0, 0,
                0, 0, radius * 1.5,
                // Pfeilspitze links-vorne
                0, 0, radius * 1.5,
                -radius * 0.3, 0, radius * 1.0,
                // Pfeilspitze rechts-vorne
                0, 0, radius * 1.5,
                radius * 0.3, 0, radius * 1.0,
              ])}
              itemSize={3}
            />
          </bufferGeometry>
          <lineBasicMaterial color="#ffffff" linewidth={2} />
        </line>

        {/* Umrandung */}
        <mesh position={[0, 0, -1]}>
          <ringGeometry args={[radius * 1.2, radius * 1.4, 32]} />
          <meshBasicMaterial color="#7c2d12" />
        </mesh>
      </group>
    );
  }

  // Arc-Zwischenpunkte: Kleine dezente Kreise (nicht prominent)
  if (isArcIntermediate) {
    return (
      <group position={[point.x, point.y, 4]}>
        <Circle
          ref={meshRef}
          args={[radius * 0.6, 16]}
          onClick={handleClick}
          onPointerDown={handlePointerDown}
          onPointerOver={handlePointerOver}
          onPointerOut={handlePointerOut}
        >
          <meshBasicMaterial
            color={getColor()}
            transparent
            opacity={0.6}
          />
        </Circle>
      </group>
    );
  }

  // Arc-Endpunkte (Start/Ende): Kreis mit Pfeilen nach innen/außen
  if (isArcEndpoint) {
    return (
      <group position={[point.x, point.y, 5]}>
        {/* Äußerer Ring */}
        <Circle
          ref={meshRef}
          args={[radius * 1.2, 32]}
          onClick={handleClick}
          onPointerDown={handlePointerDown}
          onPointerOver={handlePointerOver}
          onPointerOut={handlePointerOut}
        >
          <meshBasicMaterial
            color={getColor()}
            transparent
            opacity={0.9}
          />
        </Circle>

        {/* Innerer Punkt */}
        <Circle args={[radius * 0.4, 16]} position={[0, 0, 1]}>
          <meshBasicMaterial color="#0e7490" />
        </Circle>

        {/* Doppelpfeil-Symbol (horizontal) für "ziehbar" */}
        <line position={[0, 0, 2]}>
          <bufferGeometry>
            <bufferAttribute
              attach="attributes-position"
              count={6}
              array={new Float32Array([
                // Pfeil nach links
                -radius * 0.8, 0, 0,
                -radius * 0.3, 0, 0,
                -radius * 0.5, radius * 0.2, 0,
                -radius * 0.8, 0, 0,
                -radius * 0.5, -radius * 0.2, 0,
                -radius * 0.8, 0, 0,
              ])}
              itemSize={3}
            />
          </bufferGeometry>
          <lineBasicMaterial color="#ffffff" linewidth={2} />
        </line>
        <line position={[0, 0, 2]}>
          <bufferGeometry>
            <bufferAttribute
              attach="attributes-position"
              count={6}
              array={new Float32Array([
                // Pfeil nach rechts
                radius * 0.8, 0, 0,
                radius * 0.3, 0, 0,
                radius * 0.5, radius * 0.2, 0,
                radius * 0.8, 0, 0,
                radius * 0.5, -radius * 0.2, 0,
                radius * 0.8, 0, 0,
              ])}
              itemSize={3}
            />
          </bufferGeometry>
          <lineBasicMaterial color="#ffffff" linewidth={2} />
        </line>

        {/* Umrandung */}
        <mesh position={[0, 0, -1]}>
          <ringGeometry args={[radius * 1.1, radius * 1.3, 32]} />
          <meshBasicMaterial color="#155e75" />
        </mesh>
      </group>
    );
  }

  // Kontrollpunkte (für Geometrie) als Quadrat darstellen
  if (isControlPoint) {
    return (
      <group position={[point.x, point.y, 6]}>
        {/* Quadrat */}
        <mesh
          ref={meshRef}
          onClick={handleClick}
          onPointerDown={handlePointerDown}
          onPointerOver={handlePointerOver}
          onPointerOut={handlePointerOut}
        >
          <planeGeometry args={[radius * 1.8, radius * 1.8]} />
          <meshBasicMaterial
            color={getColor()}
            transparent
            opacity={0.9}
          />
        </mesh>

        {/* Innerer Punkt */}
        <mesh position={[0, 0, 1]}>
          <planeGeometry args={[radius * 0.6, radius * 0.6]} />
          <meshBasicMaterial color="#166534" />
        </mesh>

        {/* Umrandung */}
        <lineLoop position={[0, 0, 2]}>
          <bufferGeometry>
            <bufferAttribute
              attach="attributes-position"
              count={4}
              array={new Float32Array([
                radius * 0.95, radius * 0.95, 0,
                -radius * 0.95, radius * 0.95, 0,
                -radius * 0.95, -radius * 0.95, 0,
                radius * 0.95, -radius * 0.95, 0,
              ])}
              itemSize={3}
            />
          </bufferGeometry>
          <lineBasicMaterial color="#14532d" linewidth={2} />
        </lineLoop>
      </group>
    );
  }

  // Hilfspunkte als Raute/Diamant darstellen
  if (isAuxiliary) {
    return (
      <group position={[point.x, point.y, 5]} rotation={[0, 0, Math.PI / 4]}>
        {/* Raute (gedrehtes Quadrat) */}
        <mesh
          ref={meshRef}
          onClick={handleClick}
          onPointerDown={handlePointerDown}
          onPointerOver={handlePointerOver}
          onPointerOut={handlePointerOut}
        >
          <planeGeometry args={[radius * 1.6, radius * 1.6]} />
          <meshBasicMaterial
            color={getColor()}
            transparent
            opacity={0.9}
          />
        </mesh>

        {/* Innerer Punkt */}
        <mesh position={[0, 0, 1]}>
          <planeGeometry args={[radius * 0.6, radius * 0.6]} />
          <meshBasicMaterial color="#5b21b6" />
        </mesh>

        {/* Umrandung */}
        <lineLoop position={[0, 0, 2]}>
          <bufferGeometry>
            <bufferAttribute
              attach="attributes-position"
              count={4}
              array={new Float32Array([
                radius * 0.85, 0, 0,
                0, radius * 0.85, 0,
                -radius * 0.85, 0, 0,
                0, -radius * 0.85, 0,
              ])}
              itemSize={3}
            />
          </bufferGeometry>
          <lineBasicMaterial color="#4c1d95" linewidth={2} />
        </lineLoop>
      </group>
    );
  }

  // Standard-Wandpunkte als Kreis
  return (
    <group position={[point.x, point.y, 5]}>
      {/* Äußerer Ring */}
      <Circle
        ref={meshRef}
        args={[radius, 32]}
        onClick={handleClick}
        onPointerDown={handlePointerDown}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
      >
        <meshBasicMaterial
          color={getColor()}
          transparent
          opacity={0.9}
        />
      </Circle>

      {/* Innerer Punkt */}
      <Circle args={[radius * 0.4, 16]} position={[0, 0, 1]}>
        <meshBasicMaterial color="#1e3a8a" />
      </Circle>

      {/* Umrandung */}
      <mesh position={[0, 0, -1]}>
        <ringGeometry args={[radius * 0.9, radius * 1.1, 32]} />
        <meshBasicMaterial color="#374151" />
      </mesh>
    </group>
  );
}
